import React, { useState } from 'react'

import {
  post,
  ApiResponse,
} from 'utils/apis/insuranceLounge'

import { validate } from 'lib/validation'

import { Value } from 'components/base/Form/Input/types'

import {
  ContainerProps,
  OnSubmitFunc,
} from './types'

import EmailForm from './index.presentational'

const EmailFormContainer: React.FC<ContainerProps> = ({
  postUrl = '/contact/ask-a-question',
  showConsentToMarketingMessages = false,
  showPhoneNumber = false,
  showStatesInteresetedIn = false,
  topic: forcedTopic = null,
  ...props
}): React.ReactElement => {
  const [
    comments,
    setComments,
  ] = useState('' as Value)
  const [
    emailAddress,
    setEmailAddress,
  ] = useState('' as Value)
  const [
    name,
    setName,
  ] = useState('' as Value)
  const [
    phoneNumber,
    setPhoneNumber,
  ] = useState('' as Value)
  const [
    statesInteresetedIn,
    setStatesInteresetedIn,
  ] = useState('' as Value)
  const [
    topic,
    setTopic,
  ] = useState(forcedTopic || '' as Value)
  const [
    consentToMarketingMessages,
    setConsentToMarketingMessages,
  ] = useState(showConsentToMarketingMessages ? true as Value : undefined)

  const [
    isSubmitSuccess,
    setSubmitSuccess,
  ] = useState(false)

  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false)

  const onSubmit: OnSubmitFunc = () => {
    const error = validateForm()

    if (error) {
      return Promise.reject(error)
    }

    setIsSubmitting(true)

    return post(`/${postUrl.replace(/^\//, '')}`, {
      consentToMarketingMessages,
      comments,
      email_address: emailAddress, // eslint-disable-line @typescript-eslint/camelcase
      name,
      phone_number: phoneNumber, // eslint-disable-line @typescript-eslint/camelcase
      states_interested_in: statesInteresetedIn, // eslint-disable-line @typescript-eslint/camelcase
      topic,
    })
      .then((resp: ApiResponse) => {
        if (!resp.success) {
          return Promise.reject(resp.firstErrorMessage)
        }

        setSubmitSuccess(true)

        return Promise.resolve(true)
      })
      .finally(() => setIsSubmitting(false))
  }

  const validateForm = () => {
    const fields = [
      {
        name: 'name',
        rules: 'required|match:pattern=^[^ ]+ [^ ]+$',
        value: name,
        validationErrorMessage: 'Please enter a valid first and last name.',
      },
      {
        name: 'emailAddress',
        rules: 'required|email',
        value: emailAddress,
        validationErrorMessage: 'Please enter a valid email address.',
      },
      {
        name: 'phoneNumber',
        rules: showPhoneNumber ? 'required|phoneNumber' : '',
        value: phoneNumber,
        validationErrorMessage: 'Please enter a valid phone number.',
      },
      {
        name: 'statesInteresetedIn',
        rules: showStatesInteresetedIn ? 'required' : '',
        value: statesInteresetedIn,
        validationErrorMessage: 'Please enter a list of states you are interested.',
      },
      {
        name: 'topic',
        rules: 'required',
        value: topic,
        validationErrorMessage: 'Please select a topic area.',
      },
      {
        name: 'comments',
        rules: 'required',
        value: comments,
        validationErrorMessage: 'Please enter a comment.',
      },
      {
        name: 'consentToMarketingMessages',
        rules: showConsentToMarketingMessages ? '' : '',
        value: consentToMarketingMessages,
      },
    ]
      .map(field => ({
        ...field,
        validation: validate(field.rules, field.value),
      }))

    const firstInvalidField = fields.find(field => !field.validation.isValid)

    if (firstInvalidField) {
      return firstInvalidField.validationErrorMessage
    }

    return undefined
  }

  return React.createElement(
    EmailForm,
    {
      ...props,
      comments,
      consentToMarketingMessages,
      emailAddress,
      isSubmitSuccess,
      isSubmitting,
      isTopicForced: !!forcedTopic,
      name,
      onSubmit,
      phoneNumber,
      setComments,
      setConsentToMarketingMessages,
      setEmailAddress,
      setName,
      setPhoneNumber,
      setStatesInteresetedIn,
      setTopic,
      showConsentToMarketingMessages,
      showPhoneNumber,
      showStatesInteresetedIn,
      statesInteresetedIn,
      topic,
    },
  )
}

export default EmailFormContainer
